// src/components/simulator/engines/RentToOwnStockEngine.js

export const simulateRentToOwnStock = (params) => {
    const {
        propertyValue,
        propertyGrowthRate,
        rentGrowthRate,
        taxRate,
        insuranceRate,
        maintenancePercentOfRent,
        rentPercent,
        equityPercent,
        sellCostRate,
        monthlyRentRatio,
        stockGrowthRate,
        reinvestmentSplit,
    } = params;

    const stockMonthlyGrowthRate = Math.pow(1 + stockGrowthRate / 100, 1 / 12);
    let totalStockShares = 0;
    let currentStockPrice = 100;

    // Convert % to decimals
    const rentPortion = rentPercent / 100;
    const equityPortion = equityPercent / 100;
    const taxRateMonthly = taxRate / 100 / 12;
    const insuranceRateMonthly = insuranceRate / 100 / 12;
    const maintenanceRate = maintenancePercentOfRent / 100;
    const sellCostRateDecimal = sellCostRate / 100;

    const monthlyData = [];
    const reinvestmentChunks = [];
    const nonReinvestmentChunks = [];

    let assetValue = propertyValue;
    let monthlyRent = assetValue / monthlyRentRatio;
    let equityInvestor = propertyValue;
    let equityEntrepreneur = 0;

    let cumNetRentIncome = 0;
    let cumRentIncome = 0;
    let cumInvestorExpenses = 0;
    let cumEntrepreneurExpenses = 0;
    let cumEquityBuyout = 0;
    let cumOffsetEquity = 0;
    let cumEquityFromExtra = 0;


    let reinvestedCapital = 0;
    let nonReinvestedCash = 0;
    for (let month = 1; month <= 480; month++) {
        // ✅ Clamp negative equity
        if (equityInvestor < 0) equityInvestor = 0;
        if (equityEntrepreneur < 0) equityEntrepreneur = 0;
        let equityFromRent = monthlyRent * (1 - rentPortion);
        let equityFromExtra = monthlyRent * equityPortion;
        let rentCollected = monthlyRent * rentPortion;
        let totalEquity = equityInvestor + equityEntrepreneur;
        let investorShare = totalEquity > 0 ? equityInvestor / totalEquity : 1;
        let entrepreneurShare = 1 - investorShare;
        let entrepreneurOffset = entrepreneurShare * rentCollected;
        let investorRentIncome = rentCollected - entrepreneurOffset;

        let offsetEquity = entrepreneurOffset;

        let investorTax = assetValue * taxRateMonthly * investorShare;
        let investorInsurance = assetValue * insuranceRateMonthly * investorShare;
        let investorMaintenance = monthlyRent * maintenanceRate * investorShare;
        let investorNet = investorRentIncome - investorTax - investorInsurance - investorMaintenance;

        let entrepreneurTax = assetValue * taxRateMonthly * entrepreneurShare;
        let entrepreneurInsurance = assetValue * insuranceRateMonthly * entrepreneurShare;
        let entrepreneurMaintenance = monthlyRent * maintenanceRate * entrepreneurShare;
        let entrepreneurPayment = investorRentIncome + equityFromExtra + offsetEquity;

        let investorEquityRatio = equityInvestor / (equityInvestor + equityEntrepreneur);
        const remainingInvestorEquity = (equityInvestor + equityEntrepreneur) * investorEquityRatio;
        const currentEntrepreneurPayment = (equityFromExtra + offsetEquity + investorRentIncome);

        let totalEquityBuyout = equityFromExtra + offsetEquity;
        
        if (remainingInvestorEquity <= currentEntrepreneurPayment) {
            //assetValue *= (1 + propertyGrowthRate / 100);
            monthlyRent = 0;
            offsetEquity = remainingInvestorEquity;
            equityFromExtra = 0;
            rentCollected = 0;
            investorRentIncome = 0;
            entrepreneurTax = entrepreneurTax + investorTax;
            investorTax = 0;
            entrepreneurInsurance = entrepreneurInsurance + investorInsurance;
            investorInsurance = 0;
            entrepreneurMaintenance = entrepreneurMaintenance + investorMaintenance;
            investorMaintenance = 0;
            investorNet = 0;
            equityInvestor = 0;
            investorEquityRatio = equityInvestor / (equityInvestor + equityEntrepreneur);
            totalEquityBuyout = remainingInvestorEquity;
            investorShare = totalEquity > 0 ? equityInvestor / totalEquity : 1;
            cumEquityBuyout += remainingInvestorEquity;
            entrepreneurShare = 1 - investorShare;
        }
        else {
            equityInvestor -= totalEquityBuyout;
            equityEntrepreneur += totalEquityBuyout;
            cumEquityBuyout += totalEquityBuyout;
        }
        cumRentIncome += investorRentIncome;
        cumNetRentIncome += investorNet;
        cumInvestorExpenses += investorTax + investorInsurance + investorMaintenance;
        cumEntrepreneurExpenses += entrepreneurTax + entrepreneurInsurance + entrepreneurMaintenance;

        if (month % 12 === 1 && month > 1) {
            assetValue *= (1 + propertyGrowthRate / 100);
            monthlyRent = assetValue / monthlyRentRatio + (monthlyRent * rentGrowthRate / 100);
        }

        //const totalEquity = equityInvestor + equityEntrepreneur;
        const investorRatio = equityInvestor / totalEquity;
        const entrepreneurRatio = 1 - investorRatio;

        equityInvestor = assetValue * investorRatio;
        equityEntrepreneur = assetValue * entrepreneurRatio;

        // ✅ Move exit calculations ABOVE push()
        const closingCost = assetValue * sellCostRateDecimal;

        // const investorExitValue =
        //     (assetValue - closingCost) * investorShare +
        //     cumNetRentIncome + cumEquityBuyout;

        const entrepreneurExitValue = Math.max(
            0,
            (assetValue - closingCost) * entrepreneurShare - cumEntrepreneurExpenses
        );

        // Compute reinvestmentPool and reinvestmentPoolAgeBucket
        let reinvestmentPool = 0;
        

        //const totalBuyout = investorNet + adjustedBuyout;
        let reinvestAmount = (totalEquityBuyout + investorNet) * (reinvestmentSplit / 100);
        let nonReinvestedAmount = (totalEquityBuyout + investorNet)* (1 - reinvestmentSplit / 100);

        // Buy stock with equity buyout amount
        const stockPurchased = reinvestAmount / currentStockPrice;
        totalStockShares += stockPurchased;
        const investorStockValue = totalStockShares * currentStockPrice;

        const gramsBought = reinvestAmount / currentStockPrice;


        // if (!reinvestmentChunks) reinvestmentChunks = [];
        // reinvestmentChunks.push({
        //     month,
        //     grams: gramsBought,
        //     price: currentStockPrice,
        // });


        if (reinvestAmount > 0) {
            reinvestmentChunks.push({
                stockShares: +stockPurchased,
                amount: +reinvestAmount,
                age: null,
                monthAdded: month,
                sourceNet: +investorNet.toFixed(2),
                sourceEquity: +totalEquityBuyout.toFixed(2)
            });
        }

        // ✅ track uninvested portion as retained cash
        nonReinvestedCash += +nonReinvestedAmount.toFixed(2);

        if (nonReinvestedAmount > 0) {
            nonReinvestmentChunks.push({
                amount: +nonReinvestedAmount.toFixed(2),
                monthAdded: month,
            });
        }


        
        reinvestedCapital += +reinvestAmount;

        const entrepreneurOwnership = equityEntrepreneur / (equityInvestor + equityEntrepreneur);
        const investorExitValue = (assetValue - closingCost) * investorShare + cumNetRentIncome + cumEquityBuyout + reinvestedCapital;
        const entrepreneurROI = (cumEquityFromExtra + cumOffsetEquity) + cumEntrepreneurExpenses > 0
            ? ((assetValue - closingCost) * entrepreneurShare) / ((cumEquityFromExtra + cumOffsetEquity) + cumEntrepreneurExpenses)
            : 0;

        monthlyData.push({
            month,
            assetValue: +assetValue.toFixed(2),
            monthlyRent: +monthlyRent.toFixed(2),
            "investorShare (%)": +(investorShare * 100).toFixed(4),
            "entrepreneurShare (%)": +(entrepreneurShare * 100).toFixed(4),
            investorEquity: +equityInvestor.toFixed(2),
            entrepreneurEquity: +equityEntrepreneur.toFixed(2),
            investorRentIncome: +investorRentIncome.toFixed(2),
            investorNetCashflow: +investorNet.toFixed(2),
            equityFromExtra: +equityFromExtra.toFixed(2),
            offsetEquity: +offsetEquity.toFixed(2),
            totalEquityBuyout: +totalEquityBuyout.toFixed(2),
            investorTax: +investorTax.toFixed(2),
            investorInsurance: +investorInsurance.toFixed(2),
            investorMaintenance: +investorMaintenance.toFixed(2),
            entrepreneurTax: +entrepreneurTax.toFixed(2),
            entrepreneurInsurance: +entrepreneurInsurance.toFixed(2),
            entrepreneurMaintenance: +entrepreneurMaintenance.toFixed(2),
            entrepreneurPayment: +entrepreneurPayment.toFixed(2),
            cumulativeNetRentIncome: +cumNetRentIncome.toFixed(2),
            cumulativeInvestorExpenses: +cumInvestorExpenses.toFixed(2),
            cumulativeEntrepreneurExpenses: +cumEntrepreneurExpenses.toFixed(2),
            cumulativeEquityBuyout: +cumEquityBuyout.toFixed(2),
            cumulativeRentIncome: +cumRentIncome.toFixed(2),
            closingCost: +closingCost.toFixed(2),
            investorExitValue: +investorExitValue.toFixed(2),
            entrepreneurExitValue: +entrepreneurExitValue.toFixed(2),
            reinvestedCapital: +reinvestedCapital.toFixed(2),
            nonReinvestedCash: +nonReinvestedCash.toFixed(2),
            entrepreneurROI: +entrepreneurROI.toFixed(4),
            reinvestmentPool: +reinvestmentPool.toFixed(2),
            stockPurchased: +stockPurchased,
            totalStockShares: +totalStockShares.toFixed(4),
            stockValueAtExit: +investorStockValue.toFixed(2),
            stockPrice: +currentStockPrice,
        });

       
        // console.log(`[Month ${month}]`);
        //     console.log("Net Rent Income:", investorNet.toFixed(2));
        //     console.log("Equity From Extra:", adjustedEquityFromExtra.toFixed(2));
        //     console.log("Offset Equity:", adjustedOffsetEquity.toFixed(2));
        //     console.log("Total Equity Buyout:", totalEquityBuyout.toFixed(2));
        //     console.log("Cumulative Rent:", cumNetRentIncome.toFixed(2));
        //     console.log("Cumulative Equity:", (cumEquityFromExtra).toFixed(2));
        //     console.log("Cumulative Offset Equity:", cumOffsetEquity.toFixed(2));
        //     console.log("Total Investor Value This Month:", (cumNetRentIncome + cumEquityFromExtra + cumOffsetEquity).toFixed(2));
        //     console.log("---------------------------------------------------");
        currentStockPrice *= stockMonthlyGrowthRate;

        if (entrepreneurOwnership >= 1) break;
    }
    let final = monthlyData[monthlyData.length - 1];

    const termYears = final.month / 12;
    const trueInvestorROI = Math.pow((cumNetRentIncome + cumEquityBuyout + cumOffsetEquity) / propertyValue, 1 / termYears) - 1;

    const reinvestmentPoolAgeBucket = reinvestmentChunks.map(chunk => {
        const age = final.month - chunk.monthAdded;
        const stockPrice = final.stockPrice;
        const netAmount = chunk.stockShares * stockPrice;

        return {
            stockShares: +chunk.stockShares,
            amount: +netAmount.toFixed(2),
            age: +age,
            monthAdded: chunk.monthAdded,
            sourceNet: +chunk.sourceNet,
            sourceEquity: +chunk.sourceEquity
        };
    });


    //reinvestedCapital = reinvestmentPoolAgeBucket.reduce((sum, chunk) => sum + chunk.amount, 0);
    //reinvestedCapital = reinvestmentPoolAgeBucket.reduce((sum, chunk) => sum + chunk.amount, 0);
    final.reinvestmentPoolAgeBucket = reinvestmentPoolAgeBucket;
    //final.reinvestedCapital = +reinvestedCapital.toFixed(2);


    const asset = final.assetValue;
    const year = final.month / 12 + 1;
    const ownership = final.investorEquity / asset;
    const sellCost = year > 1 ? asset * sellCostRateDecimal : 0;

    const reinvestedPortion = reinvestmentPoolAgeBucket
        //.filter(r => r.age >= (final.month - ((year - 1) * 12)))
        .reduce((sum, r) => sum + r.netAmount, 0);

    // const investorExit = (asset - sellCost) * ownership
    //                    + last.cumulativeNetRentIncome
    //                    + last.cumulativeEquityBuyout
    //                    + reinvestedPortion;
    // const investorExit = (asset - sellCost) * ownership
    //     + reinvestedPortion;


    const investorExit = (asset - sellCost) * ownership
        + final.stockValueAtExit
        + final.nonReinvestedCash;
    final.investorExitValue = investorExit.toFixed(2);





    // const totalReinvestedCapitalFromMonthlyData = monthlyData.reduce(
    //     (sum, m) => sum + m.investorRentIncome - m.investorInsurance - m.investorTax - m.investorInsurance + m.equityFromExtra + m.offsetEquity,
    //     0
    // );

    // const totalReinvestedCapitalFromMonthlyData = reinvestmentPoolAgeBucket.reduce(
    //     (sum, m) => sum + m.amount,
    //     0
    // );

    // const totalReinvestedCapitalFromMonthlyData = monthlyData.reduce(
    //     (sum, m) => sum + m.stockPurchased,
    //     0
    // );


    const totalReinvestedCapital = reinvestmentChunks.reduce((sum, chunk) => sum + chunk.amount, 0);

    const equityBuyoutAudit = [];
    let runningCumulative = 0;

    monthlyData.forEach((m, idx) => {
        const prevCumulative = runningCumulative;
        runningCumulative += m.totalEquityBuyout;

        equityBuyoutAudit.push({
            month: m.month,
            equityBuyout: m.totalEquityBuyout,
            expectedCumulative: +runningCumulative.toFixed(2),
            recordedCumulative: m.cumulativeEquityBuyout,
            delta: +(m.cumulativeEquityBuyout - prevCumulative - m.totalEquityBuyout).toFixed(4)
        });
    });

    const sumOfBuyouts = monthlyData.reduce((sum, m) => sum + m.totalEquityBuyout, 0);

    // console.log('Equity Buyout Audit Table:', equityBuyoutAudit);
    // console.log('Summation of totalEquityBuyout:', +sumOfBuyouts.toFixed(2));



    const yearlyData = [];
    let accumulatedStockShares = 0;
    for (let i = 0; i < monthlyData.length; i += 12) {
        const chunk = monthlyData.slice(i, i + 12);
        const last = chunk[chunk.length - 1];
        // const yearShares = monthlyData
        //     .reduce((sum, r) => sum + r.stockPurchased, 0);
        const yearShares = chunk.reduce((sum, r) => sum + r.stockPurchased, 0);
        accumulatedStockShares += +yearShares;
        const stockExitValue = accumulatedStockShares * last.stockPrice;
        const exitValue = (last.assetValue - last.closingCost) * (last.investorEquity / last.assetValue) +
            stockExitValue + last.nonReinvestedCash;
        // console.log("asset ", (last.assetValue - last.closingCost) * (last.investorEquity / last.assetValue) );
        // console.log("stock", stockExitValue);
        // console.log("cash", last.nonReinvestedCash);


        yearlyData.push({
            year: i / 12 + 1,
            assetValue: last.assetValue,
            investorEquity: last.investorEquity,
            entrepreneurEquity: last.entrepreneurEquity,
            stockShares: +yearShares.toFixed(4),
            stockExitValue: +stockExitValue.toFixed(2),
            investorExitValue: +exitValue.toFixed(2),
            entrepreneurExitValue: last.entrepreneurExitValue,
            rentIncome: chunk.reduce((sum, m) => sum + m.investorRentIncome, 0),
            investorNet: chunk.reduce((sum, m) => sum + m.investorNetCashflow, 0),
            equityBuyout: chunk.reduce((sum, m) => sum + m.totalEquityBuyout, 0),
            entrepreneurPaid: chunk.reduce((sum, m) => sum + m.entrepreneurPayment, 0),
            investorTax: chunk.reduce((sum, m) => sum + m.investorTax, 0),
            investorInsurance: chunk.reduce((sum, m) => sum + m.investorInsurance, 0),
            investorMaintenance: chunk.reduce((sum, m) => sum + m.investorMaintenance, 0),
            entrepreneurTax: chunk.reduce((sum, m) => sum + m.entrepreneurTax, 0),
            entrepreneurInsurance: chunk.reduce((sum, m) => sum + m.entrepreneurInsurance, 0),
            entrepreneurMaintenance: chunk.reduce((sum, m) => sum + m.entrepreneurMaintenance, 0),
            nonReinvestedCash: last.nonReinvestedCash,
            reinvestedCapital: last.reinvestedCapital,
            totalStockShares: accumulatedStockShares.toFixed(4),
            cumulativeInvestorExpenses: last.cumulativeInvestorExpenses,
            cumulativeEntrepreneurExpenses: last.cumulativeEntrepreneurExpenses,
        });
    }

    final = monthlyData[monthlyData.length - 1];

    // console.log("final", final);
    const totalROI = (final.investorExitValue - propertyValue) / propertyValue;
    
    const capRate = ((final.cumulativeNetRentIncome / (final.month / 12)) / propertyValue) * 100;
    const stockValueAtExit = final.stockPrice * accumulatedStockShares;
    const invstExitValue = (stockValueAtExit + final.nonReinvestedCash).toFixed(2);

    
    const annualizedROI = (Math.pow(1 + totalROI, 1 / (final.month / 12)) - 1) * 100;
    const irrEstimate = annualizedROI; // Approximation (valid since cashflows are even)

    // investorResult.annualizedROI = +annualizedROI.toFixed(2);
    // investorResult.irrEstimate = +irrEstimate.toFixed(2);

    const operatingExpenses = monthlyData.reduce((sum, m) =>
        sum + m.investorTax + m.investorInsurance + m.investorMaintenance, 0
    );

    const noi = final.cumulativeRentIncome - operatingExpenses;

    const annualNOI = noi / (final.month / 12);
    

   

    const investorResult = {
        model: "Rent-to-Own + Stock",
        originalPropertyValue: propertyValue,
        finalValue: final.assetValue ?? 0,
        totalStockShares: final.totalStockShares != null ? +final.totalStockShares.toFixed(4) : 0,
        stockValueAtExit: +stockValueAtExit.toFixed(2),
        reinvestedCapital: final.reinvestedCapital != null ? +final.reinvestedCapital.toFixed(2) : 0,
        nonReinvestedCash: final.nonReinvestedCash != null ? +final.nonReinvestedCash.toFixed(2) : 0,
        exitValue: invstExitValue,
        netCashflow: final.cumulativeNetRentIncome != null ? +final.cumulativeNetRentIncome.toFixed(2) : 0,
        totalExpenses: final.cumulativeInvestorExpenses != null ? +final.cumulativeInvestorExpenses.toFixed(2) : 0,
        totalROI: totalROI != null ? +(totalROI * 100).toFixed(2) : 0,
        annualizedROI: annualizedROI != null ? +(annualizedROI * 100).toFixed(2) : 0,
        irrEstimate: irrEstimate.toFixed(2),
        annualNOI: Math.round(annualNOI),
        capRate: capRate != null ? +capRate.toFixed(2) : 0,
        cashOnCashReturn: final.cumulativeNetRentIncome != null
            ? +((final.cumulativeNetRentIncome / propertyValue) * 100).toFixed(2)
            : 0,
        termMonths: final.month ?? 0,
        termYears: final.month != null ? +(final.month / 12).toFixed(1) : 0,
        totalRentCollected: final.cumulativeRentIncome ?? 0,
    };


    const borrowerResult = {
        model: "Rent-to-Own + Stock",
        equityBuilt: final.entrepreneurEquity,
        exitValue: final.entrepreneurExitValue,
        totalRentPaid: final.cumulativeRentIncome,
        totalExpenses: final.cumulativeEntrepreneurExpenses,        
        totalBuyout: +(monthlyData.reduce((sum, m) => sum + m.totalEquityBuyout, 0)).toFixed(2),
        totalPayment: +(final.cumulativeRentIncome + final.cumulativeEntrepreneurExpenses + final.cumulativeEquityBuyout).toFixed(2),
        monthlyRentPayment: +(monthlyData.reduce((sum, m) => sum + m.investorRentIncome, 0) / final.month).toFixed(2),
        monthlyPayment: +(monthlyData.reduce((sum, m) => sum + m.totalEquityBuyout + m.investorRentIncome, 0) / final.month).toFixed(2),
        entrepreneurROI: final.entrepreneurROI,
        termMonths: final.month,
        termYears: +(final.month / 12).toFixed(1),
    };

   

    // console.log(monthlyData);
    // console.log('Final Pool:', reinvestmentChunks);
    // console.log('Updated pool:', reinvestmentPoolAgeBucket);
    // console.log('cumNetRentIncome:', cumNetRentIncome);
    // console.log('cumEquityBuyout:', cumEquityBuyout);
    // console.log('cumOffsetEquity:', cumOffsetEquity);
    // console.log('propertyValue:', propertyValue);
    // console.log('trueInvestorROI:', trueInvestorROI);
    // //console.log('totalReinvestedCapitalFromMonthlyData:', totalReinvestedCapitalFromMonthlyData);
    // console.log('ent equityBuilt:', final.entrepreneurEquity);
    // console.log('totalReinvestedCapital:', totalReinvestedCapital);

    //console.log(monthlyData);

    return { monthlyData, yearlyData, investorResult, borrowerResult };

};
